import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Descarga un documento del SGSI enviando el token. Un <a href> plano no manda la
   cabecera Authorization y el backend responde "Not authenticated". */
const downloadIsoDoc = async (doc) => {
  try {
    const token = localStorage.getItem("token");
    const res = await axios.get(`${API}/iso27001/documents/${doc.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.filename || doc.title || "documento";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    alert("No se pudo descargar el documento. Volvé a intentar o refrescá la sesión.");
  }
};

const CATEGORY_META = {
  organizacional: { label: "Organizacional", range: "5.1–5.37", count: 37, color: "#2563eb", bg: "bg-blue-50", text: "text-blue-700", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
  personas: { label: "Personas", range: "6.1–6.8", count: 8, color: "#16a34a", bg: "bg-green-50", text: "text-green-700", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" },
  fisico: { label: "Físico", range: "7.1–7.14", count: 14, color: "#ea580c", bg: "bg-orange-50", text: "text-orange-700", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
  tecnologico: { label: "Tecnológico", range: "8.1–8.34", count: 34, color: "#9333ea", bg: "bg-purple-50", text: "text-purple-700", icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" },
};

const STATUS_META = {
  no_evaluado: { label: "No evaluado", color: "bg-gray-100 text-gray-600", dot: "bg-gray-400" },
  planificado: { label: "Planificado", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  en_curso: { label: "En curso", color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-500" },
  implementado: { label: "Implementado", color: "bg-green-100 text-green-700", dot: "bg-green-500" },
  no_aplica: { label: "No aplica", color: "bg-slate-100 text-slate-500", dot: "bg-slate-400" },
};

const DOC_TYPES = {
  evidencia: "Evidencia",
  politica: "Politica",
  procedimiento: "Procedimiento",
  registro: "Registro",
  otro: "Otro",
};

/* ── Icon helper ── */
const CatIcon = ({ d, className = "w-5 h-5" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

/* ── Circular Progress ── */
const CircularProgress = ({ percentage, size = 120, strokeWidth = 10, color = "#2563eb" }) => {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percentage / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">


      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.8s ease" }} />
    </svg>
  );
};

/* ── SOA Detail Dialog ── */
const SOADetailDialog = ({ control, onClose, onSave, documents, onUploadDoc, onDeleteDoc }) => {
  const [form, setForm] = useState({
    applicable: control?.applicable || "pendiente",
    status: control?.status || "no_evaluado",
    justification: control?.justification || "",
    notes: control?.notes || "",
    responsible: control?.responsible || "",
    target_date: control?.target_date || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadType, setUploadType] = useState("evidencia");
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const today = new Date().toISOString().split("T")[0];

  const validateForm = () => {
    const newErrors = {};
    if (form.target_date && form.target_date < today) {
      newErrors.target_date = "La fecha no puede ser anterior a hoy";
    }
    if (["implementado", "en_curso"].includes(form.status) && !form.responsible.trim()) {
      newErrors.responsible = "Responsable obligatorio para este estado";
    }
    if (form.applicable === "no" && !form.justification.trim()) {
      newErrors.justification = "Justificacion obligatoria cuando no aplica";
    }
    if (form.status === "implementado" && !form.target_date) {
      newErrors.target_date = "Fecha requerida para controles implementados";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/iso27001/soa/${control.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSave();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("title", uploadTitle);
      fd.append("control_id", control.id);
      fd.append("doc_type", uploadType);
      await axios.post(`${API}/iso27001/documents/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setUploadFile(null);
      setUploadTitle("");
      setUploadOpen(false);
      onUploadDoc();
    } catch (e) {
      alert("Error al subir: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const controlDocs = documents.filter((d) => d.control_id === control.id);
  const cat = CATEGORY_META[control.category] || {};

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="soa-detail-dialog">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${cat.bg} flex items-center justify-center`}>
                <span className={`text-sm font-bold ${cat.text}`}>{control.id}</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{control.name}</h2>
                <p className="text-sm text-gray-500 mt-0.5">{control.description}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Aplicable</label>
              <select value={form.applicable} onChange={(e) => setForm({ ...form, applicable: e.target.value })}
                className="w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                data-testid="soa-applicable">
                <option value="pendiente">Pendiente</option>
                <option value="si">Si</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                data-testid="soa-status">
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Responsable</label>
              <input value={form.responsible} onChange={(e) => { setForm({ ...form, responsible: e.target.value }); setErrors(prev => ({...prev, responsible: undefined})); }}
                className={`w-full h-11 rounded-lg border px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50 ${errors.responsible ? "border-red-400 bg-red-50/50" : "border-gray-200"}`}
                placeholder="Nombre del responsable" data-testid="soa-responsible" />
              {errors.responsible && <p className="text-xs text-red-500 mt-1">{errors.responsible}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha objetivo</label>
              <input type="date" value={form.target_date} onChange={(e) => { setForm({ ...form, target_date: e.target.value }); setErrors(prev => ({...prev, target_date: undefined})); }}
                min={today}
                className={`w-full h-11 rounded-lg border px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50 ${errors.target_date ? "border-red-400 bg-red-50/50" : "border-gray-200"}`}
                data-testid="soa-target-date" />
              {errors.target_date && <p className="text-xs text-red-500 mt-1">{errors.target_date}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Justificacion</label>
            <textarea value={form.justification} onChange={(e) => { setForm({ ...form, justification: e.target.value }); setErrors(prev => ({...prev, justification: undefined})); }}
              rows={2} className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50 ${errors.justification ? "border-red-400 bg-red-50/50" : "border-gray-200"}`}
              placeholder="Justifique la aplicabilidad o exclusion de este control..." data-testid="soa-justification" />
            {errors.justification && <p className="text-xs text-red-500 mt-1">{errors.justification}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Notas</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
              placeholder="Notas adicionales..." data-testid="soa-notes" />
          </div>

          {/* Documents section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Evidencias / Documentos</label>
              <button onClick={() => setUploadOpen(!uploadOpen)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                data-testid="toggle-upload">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Subir documento
              </button>
            </div>

            {uploadOpen && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="bg-gray-50 rounded-xl p-4 mb-3 space-y-3">
                <input type="text" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)}
                  className="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm" placeholder="Titulo del documento" data-testid="upload-title" />
                <div className="flex gap-3">
                  <select value={uploadType} onChange={(e) => setUploadType(e.target.value)}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-sm" data-testid="upload-type">
                    {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input type="file" onChange={(e) => setUploadFile(e.target.files[0])}
                    className="flex-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    data-testid="upload-file-input" />
                </div>
                <button onClick={handleUpload} disabled={!uploadFile || !uploadTitle || uploading}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                  data-testid="upload-submit">
                  {uploading ? "Subiendo..." : "Subir"}
                </button>
              </motion.div>
            )}

            {controlDocs.length > 0 ? (
              <div className="space-y-2">
                {controlDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                      <p className="text-xs text-gray-400">{doc.filename} - {DOC_TYPES[doc.doc_type] || doc.doc_type}</p>
                    </div>
                    <button type="button" onClick={() => downloadIsoDoc(doc)}
                      className="text-blue-600 hover:text-blue-800 p-1.5 rounded-lg hover:bg-blue-50" title="Descargar"
                      data-testid={`iso-doc-download-${doc.id}`}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    </button>
                    <button onClick={() => onDeleteDoc(doc.id)} className="text-red-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50" title="Eliminar">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">Sin documentos adjuntos para este control.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 font-medium" data-testid="soa-cancel">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2.5 text-sm bg-[#2853B9] text-white rounded-xl hover:bg-[#1E3876] disabled:opacity-50 font-medium transition-all duration-200 hover:shadow-lg active:scale-95"
            data-testid="soa-save">
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ── Main Component ── */
const ISO27001Management = ({ companyModules = {} }) => {

  // Company selector for superadmin
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [moduleStatus, setModuleStatus] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setCurrentUser(res.data);
        if (res.data.role === "superadmin") {
          axios.get(`${API}/admin/companies`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => setCompanies(r.data.filter(c => c.name !== "CostillaTech.com Platform" && c.name !== "CostillaTech.com")))
            .catch(() => {});
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCompanyId || !currentUser || currentUser.role !== "superadmin") {
      setModuleStatus(null);
      return;
    }
    const token = localStorage.getItem("token");
    axios.get(`${API}/admin/company-modules/${selectedCompanyId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setModuleStatus(res.data))
      .catch(() => setModuleStatus(null));
  }, [selectedCompanyId, currentUser]);

  // Determine if ISO templates module is enabled
  const templatesEnabled = currentUser?.role === "superadmin"
    ? true
    : (companyModules.iso27001_templates === true);



  const companyParam = () => currentUser?.role === "superadmin" && selectedCompanyId ? `company_id=${selectedCompanyId}` : "";

  const [controls, setControls] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedControl, setSelectedControl] = useState(null);
  const [gapData, setGapData] = useState(null);
  const [clausesData, setClausesData] = useState([]);
  const [gapLoading, setGapLoading] = useState(false);
  const [clauseLoading, setClauseLoading] = useState(false);
  const [clauseUploading, setClauseUploading] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const h = { Authorization: `Bearer ${token}` };
      const [cr, dr, dsr] = await Promise.all([
        axios.get(`${API}/iso27001/controls` + (companyParam() ? `?${companyParam()}` : ""), { headers: h }),
        axios.get(`${API}/iso27001/documents` + (companyParam() ? `?${companyParam()}` : ""), { headers: h }),
        axios.get(`${API}/iso27001/dashboard` + (companyParam() ? `?${companyParam()}` : ""), { headers: h }),
      ]);
      setControls(cr.data.controls || []);
      setDocuments(dr.data.documents || []);
      setDashboard(dsr.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, currentUser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchGaps = useCallback(async () => {
    try {
      setGapLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/iso27001/gap-analysis` + (companyParam() ? `?${companyParam()}` : ""), { headers: { Authorization: `Bearer ${token}` } });
      setGapData(res.data);
    } catch (err) { console.error("Error gaps:", err); } finally { setGapLoading(false); }
  }, [selectedCompanyId, currentUser]);

  const fetchClausesWithDocs = useCallback(async () => {
    try {
      setClauseLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/iso27001/clauses/with-docs` + (companyParam() ? `?${companyParam()}` : ""), { headers: { Authorization: `Bearer ${token}` } });
      setClausesData(res.data.clauses || []);
    } catch (err) { console.error("Error clauses:", err); } finally { setClauseLoading(false); }
  }, [selectedCompanyId, currentUser]);

  useEffect(() => {
    if (activeTab === "gaps") fetchGaps();
    if (activeTab === "clauses") fetchClausesWithDocs();
  }, [activeTab, fetchGaps, fetchClausesWithDocs]);

  const handleClauseUpload = async (clauseId, file) => {
    setClauseUploading(clauseId);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("clause_id", clauseId);
      fd.append("doc_type", "registro");
      await axios.post(`${API}/iso27001/documents/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      fetchClausesWithDocs();
    } catch (e) { alert("Error al subir: " + (e.response?.data?.detail || e.message)); }
    setClauseUploading(null);
  };

  const handleTemplateDownload = async (clauseNumber) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/iso27001/templates/${clauseNumber}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Ayuda_ISO27001_Clausula${clauseNumber}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.status === 403
        ? "El modulo de plantillas no esta habilitado para su empresa. Contacte al administrador."
        : err.response?.status === 404
        ? "La plantilla para esta cláusula aún no está disponible."
        : "Error al descargar: " + (err.response?.data?.detail || err.message);
      alert(msg);
    }
  };

  const handleClauseDeleteDoc = async (docId) => {
    if (!window.confirm("Eliminar este documento?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/iso27001/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchClausesWithDocs();
    } catch (e) { alert("Error al eliminar"); }
  };

  const filtered = controls.filter((c) => {
    if (filterCategory !== "all" && c.category !== filterCategory) return false;
    if (filterStatus !== "all" && c.status !== filterStatus) return false;
    if (searchTerm && !c.name.toLowerCase().includes(searchTerm.toLowerCase()) && !c.id.includes(searchTerm) && !c.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Eliminar este documento?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/iso27001/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch { alert("Error al eliminar"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="iso-loading">
        <div className="animate-spin h-10 w-10 border-4 border-[#2853B9] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="iso27001-page">
      {/* Company Selector for SuperAdmin */}
      {currentUser?.role === "superadmin" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-red-800 mb-2">Filtrar por Empresa</h3>
          <select data-testid="iso-company-selector" value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-sm">
            <option value="">Todas las empresas (consolidado)</option>
            {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          {selectedCompanyId && (
            <div className="mt-2">
              <p className="text-xs text-red-600">Mostrando: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong></p>
              {moduleStatus && !moduleStatus["iso27001"] && (
                <div className="mt-2 bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-start gap-2">
                  <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Modulo ISO 27001 no habilitado</p>
                    <p className="text-xs text-yellow-600 mt-0.5">Este modulo no esta activo para esta empresa. Puede habilitarlo desde el Panel de Administracion.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">ISO 27001:2022</h1>
          <p className="text-sm text-gray-500 mt-0.5">Sistema de Gestión de Seguridad de la Información - Anexo A</p>
        </div>
        <div className="flex items-center gap-2 bg-[#F6F8FA] rounded-xl p-1">
          {[
            { id: "dashboard", label: "Dashboard" },
            { id: "soa", label: "Controles SOA" },
            { id: "documents", label: "Documentos" },
            { id: "gaps", label: "Análisis de Gaps" },
            { id: "clauses", label: "Cláusulas SGSI" },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === tab.id ? "bg-white text-[#2853B9] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid={`tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {/* ── DASHBOARD TAB ── */}
        {activeTab === "dashboard" && dashboard && (
          <motion.div key="dashboard" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            {/* Compliance Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex flex-col lg:flex-row gap-8 items-center">
                <div className="relative">
                  <CircularProgress percentage={dashboard.compliance_percentage} size={160} strokeWidth={14} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-[#1E3876]">{dashboard.compliance_percentage}%</span>
                    <span className="text-xs text-gray-400 font-medium">Cumplimiento</span>
                  </div>
                </div>
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-4">
                  {[
                    { label: "Total", value: dashboard.total_controls, color: "text-[#1E3876]" },
                    { label: "Implementados", value: dashboard.implemented, color: "text-green-600" },
                    { label: "En curso", value: dashboard.in_progress, color: "text-yellow-600" },
                    { label: "Planificados", value: dashboard.planned, color: "text-blue-600" },
                    { label: "No evaluados", value: dashboard.not_evaluated, color: "text-gray-400" },
                  ].map((k) => (
                    <div key={k.label} className="text-center">
                      <p className={`text-3xl font-bold ${k.color}`}>{k.value}</p>
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mt-1">{k.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Category Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(CATEGORY_META).map(([key, cat]) => {
                const catData = dashboard.by_category?.[key] || {};
                return (
                  <motion.div key={key} whileHover={{ y: -2 }} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                    onClick={() => { setActiveTab("soa"); setFilterCategory(key); }}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-xl ${cat.bg} flex items-center justify-center`}>
                        <CatIcon d={cat.icon} className={`w-5 h-5 ${cat.text}`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{cat.label}</p>
                        <p className="text-xs text-gray-400">{cat.range} ({cat.count})</p>
                      </div>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-bold" style={{ color: cat.color }}>{catData.percentage || 0}%</p>
                        <p className="text-xs text-gray-400">{catData.implemented || 0} de {catData.applicable || catData.total || 0}</p>
                      </div>
                      <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${catData.percentage || 0}%`, backgroundColor: cat.color }} />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Recent Activity + Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-[#1E3876] mb-4">Actividad reciente</h3>
                {dashboard.recent_activity?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.recent_activity.slice(0, 5).map((a, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${a.action === "upload_document" ? "bg-purple-500" : a.action === "delete_document" ? "bg-red-500" : "bg-blue-500"}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 truncate">
                            <span className="font-medium">{a.control_id || ""}</span> {a.control_name}
                          </p>
                          <p className="text-xs text-gray-400">{a.user_name} - {a.timestamp ? new Date(a.timestamp).toLocaleDateString("es-AR") : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-6">Sin actividad reciente</p>
                )}
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-[#1E3876] mb-4">Documentos</h3>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center">
                    <svg className="w-7 h-7 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#1E3876]">{dashboard.documents_count || 0}</p>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Documentos subidos</p>
                  </div>
                </div>
                <button onClick={() => setActiveTab("documents")}
                  className="mt-4 w-full py-2.5 text-sm font-medium text-[#2853B9] bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                  data-testid="go-documents">
                  Ver documentos
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── SOA TAB ── */}
        {activeTab === "soa" && (
          <motion.div key="soa" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Filters */}
              <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap gap-3 items-center">
                <input placeholder="Buscar control..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-56 h-10 border border-gray-200 rounded-lg px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                  data-testid="iso-search" />
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                  className="h-10 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50/50" data-testid="iso-filter-category">
                  <option value="all">Todas las categorias</option>
                  {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label} ({v.count})</option>)}
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  className="h-10 border border-gray-200 rounded-lg px-3 text-sm bg-gray-50/50" data-testid="iso-filter-status">
                  <option value="all">Todos los estados</option>
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <span className="text-xs text-gray-400 ml-auto">{filtered.length} de {controls.length} controles</span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="iso-controls-table">
                  <thead>
                    <tr className="bg-[#F6F8FA] border-b text-xs">
                      <th className="text-left px-4 py-3 font-semibold text-gray-500 w-16">ID</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-500 w-24">Categoria</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-500">Control</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-500 w-24">Aplicable</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-500 w-28">Estado</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-500 w-28">Responsable</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-500 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 text-gray-400">Sin resultados</td></tr>
                    ) : filtered.map((ctrl) => {
                      const cat = CATEGORY_META[ctrl.category] || {};
                      const st = STATUS_META[ctrl.status] || STATUS_META.no_evaluado;
                      return (
                        <tr key={ctrl.id} className="border-b border-gray-50 hover:bg-[#F6F8FA]/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedControl(ctrl)} data-testid={`control-row-${ctrl.id}`}>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold ${cat.bg} ${cat.text}`}>{ctrl.id}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500">{cat.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-800 font-medium truncate max-w-md">{ctrl.name}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium ${ctrl.applicable === "si" ? "text-green-600" : ctrl.applicable === "no" ? "text-red-500" : "text-gray-400"}`}>
                              {ctrl.applicable === "si" ? "Si" : ctrl.applicable === "no" ? "No" : "Pendiente"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                              {st.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500 truncate block max-w-[100px]">{ctrl.responsible || "-"}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {activeTab === "documents" && (
          <motion.div key="documents" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-[#1E3876] mb-4">Repositorio de Documentos ISO 27001</h3>
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-4 p-4 bg-[#F6F8FA] rounded-xl border border-gray-100 hover:shadow-sm transition-shadow">
                      <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{doc.title}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-gray-400">{doc.filename}</span>
                          {doc.control_id && <span className="text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Control {doc.control_id}</span>}
                          <span className="text-xs text-gray-400">{DOC_TYPES[doc.doc_type] || doc.doc_type}</span>
                          <span className="text-xs text-gray-400">{doc.uploaded_by}</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => downloadIsoDoc(doc)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                        data-testid={`iso-doc-download-btn-${doc.id}`}>
                        Descargar
                      </button>
                      <button onClick={() => handleDeleteDoc(doc.id)}
                        className="p-1.5 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  <p className="text-gray-400 text-sm">No hay documentos subidos.</p>
                  <p className="text-gray-400 text-xs mt-1">Suba documentos desde la vista de cada control en la tabla SOA.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ── GAP ANALYSIS TAB ── */}
        {activeTab === "gaps" && (
          <motion.div key="gaps" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            {gapLoading ? (
              <div className="flex justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-red-500 border-t-transparent rounded-full" /></div>
            ) : gapData ? (
              <>
                {/* Summary Header */}
                <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-6 text-white">
                  <h3 className="text-lg font-bold">Análisis de Brechas (Gap Analysis)</h3>
                  <p className="text-red-200 text-sm mt-1">Controles no implementados que requieren atencion</p>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="text-4xl font-bold">{gapData.total_gaps}</span>
                    <span className="text-red-200 text-sm pb-1">brechas identificadas</span>
                  </div>
                </div>

                {/* Domain Breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(gapData.domain_summary || {}).map(([domain, data]) => {
                    const cat = CATEGORY_META[domain] || {};
                    return (
                      <div key={domain} className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow`} data-testid={`gap-domain-${domain}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 rounded-xl ${cat.bg || "bg-gray-50"} flex items-center justify-center`}>
                            {cat.icon && <CatIcon d={cat.icon} className={`w-5 h-5 ${cat.text || "text-gray-600"}`} />}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{cat.label || domain}</p>
                            <p className="text-lg font-bold" style={{ color: cat.color || "#333" }}>{data.compliance_pct}%</p>
                          </div>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 mb-3 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${data.compliance_pct}%`, backgroundColor: cat.color || "#666" }} />
                        </div>
                        <div className="grid grid-cols-4 text-center text-xs gap-1">
                          <div><p className="font-bold text-green-600">{data.implemented}</p><p className="text-gray-400">Impl.</p></div>
                          <div><p className="font-bold text-yellow-600">{data.in_progress}</p><p className="text-gray-400">Curso</p></div>
                          <div><p className="font-bold text-blue-600">{data.planned}</p><p className="text-gray-400">Plan.</p></div>
                          <div><p className="font-bold text-red-600">{data.gaps}</p><p className="text-gray-400">Gaps</p></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Gaps Table */}
                {gapData.gaps && gapData.gaps.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 bg-red-50">
                      <h4 className="font-semibold text-red-800">Controles con brechas ({gapData.gaps.length})</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="gap-table">
                        <thead>
                          <tr className="bg-gray-50 border-b text-xs">
                            <th className="text-left px-4 py-3 font-semibold text-gray-500">ID</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-500">Control</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-500">Categoria</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-500">Responsable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gapData.gaps.map((g) => {
                            const cat = CATEGORY_META[g.category] || {};
                            return (
                              <tr key={g.control_id} className="border-b border-gray-50 hover:bg-red-50/30">
                                <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold ${cat.bg || ""} ${cat.text || ""}`}>{g.control_id}</span></td>
                                <td className="px-4 py-3 text-sm text-gray-800">{g.name}</td>
                                <td className="px-4 py-3 text-xs text-gray-500">{cat.label || g.category}</td>
                                <td className="px-4 py-3 text-xs text-gray-500">{g.responsible || "Sin asignar"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {gapData.total_gaps === 0 && (
                  <div className="text-center py-12 bg-green-50 rounded-2xl border border-green-200">
                    <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <p className="text-green-700 font-semibold">Sin brechas pendientes</p>
                    <p className="text-green-600 text-sm">Todos los controles aplicables estan implementados o en progreso</p>
                  </div>
                )}
              </>
            ) : null}
          </motion.div>
        )}

        {/* ── CLAUSES SGSI TAB ── */}
        {activeTab === "clauses" && (
          <motion.div key="clauses" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            {clauseLoading ? (
              <div className="flex justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>
            ) : (
              <>
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-2xl p-6 text-white">
                  <h3 className="text-lg font-bold">Repositorio Documental - SGSI</h3>
                  <p className="text-indigo-200 text-sm mt-1">Documentación de las cláusulas 4 a 10 de ISO 27001:2022</p>
                </div>

                <div className="space-y-4">
                  {clausesData.map((clause) => (
                    <div key={clause.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`clause-${clause.id}`}>
                      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50 bg-[#F6F8FA]">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">{clause.id}</div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm">{clause.name}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">{clause.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {templatesEnabled ? (
                            <button
                              onClick={() => handleTemplateDownload(clause.id)}
                              className="text-xs font-medium px-3 py-1.5 rounded-lg transition bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1.5"
                              data-testid={`download-template-${clause.id}`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              Descargar Ayuda
                            </button>
                          ) : (
                            <span
                              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-gray-100 text-gray-400 flex items-center gap-1.5 cursor-not-allowed"
                              title="Función premium. Contacte al administrador para habilitarla."
                              data-testid={`download-template-pro-${clause.id}`}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                              Ayuda
                              <span className="bg-amber-400 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">PRO</span>
                            </span>
                          )}
                          <label className={`text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer transition flex-shrink-0 ${clauseUploading === clause.id ? "bg-gray-100 text-gray-400" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`} data-testid={`upload-clause-${clause.id}`}>
                            {clauseUploading === clause.id ? "Subiendo..." : "+ Documento"}
                            <input type="file" className="hidden" onChange={(e) => { if (e.target.files[0]) handleClauseUpload(clause.id, e.target.files[0]); e.target.value = ""; }} disabled={clauseUploading === clause.id} />
                          </label>
                        </div>
                      </div>
                      <div className="px-5 py-3">
                        {(clause.documents || []).length > 0 ? (
                          <div className="space-y-2">
                            {clause.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100" data-testid={`clause-doc-${doc.id}`}>
                                <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                                  <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">{doc.title || doc.filename}</p>
                                  <p className="text-xs text-gray-400">{doc.filename} - {doc.uploaded_by}</p>
                                </div>
                                <button type="button" onClick={() => downloadIsoDoc(doc)} className="px-2 py-1 text-xs text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100" data-testid={`iso-clause-doc-download-${doc.id}`}>Descargar</button>
                                <button onClick={() => handleClauseDeleteDoc(doc.id)} className="text-red-400 hover:text-red-600 p-1">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 text-center py-3">Sin documentos cargados para esta cláusula</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* SOA Detail Dialog */}
      <AnimatePresence>
        {selectedControl && (
          <SOADetailDialog
            control={selectedControl}
            onClose={() => setSelectedControl(null)}
            onSave={() => { setSelectedControl(null); fetchData(); }}
            documents={documents}
            onUploadDoc={fetchData}
            onDeleteDoc={handleDeleteDoc}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ISO27001Management;
